import { Injectable } from '@angular/core';
import { Platform, ToastController } from '@ionic/angular';
import { ScanQr } from '../classes/ScanQr/scan-qr';
import { Toast } from '@capacitor/toast';
import { App } from '@capacitor/app';
import { Device, DeviceId, OperatingSystem } from '@capacitor/device';
import { KeepAwake } from '@capacitor-community/keep-awake';
import * as CryptoJS from 'crypto-js';
import { NativeSettings, IOSSettings, AndroidSettings } from 'capacitor-native-settings';
import { Diagnostic } from '@awesome-cordova-plugins/diagnostic/ngx';
import { Capacitor } from '@capacitor/core';
import { LoggerService } from './logger.service';

@Injectable({
  providedIn: 'root'
})
export class ToolsService {
  private version: string = "browser"
  private platform = Capacitor.getPlatform()
  private ready: boolean = false
  private UUID: string = "";
  private cachedDeviceInfo = null
  constructor(private scanQr: ScanQr, private plt: Platform, private toastBrowser: ToastController, private diagnostic: Diagnostic) {
    this.plt.ready().then(this.initate.bind(this))
  }

  get ScanQr(): ScanQr {
    return this.scanQr
  }


  private initate() {
    this.ready = true
    // this.audio.initate()
    if (!this.isPlatform("web")) {
      App.getInfo().then(info => {
        this.version = info.version
      })
    } else {
      this.version = "1.0.35"
    }


  }

  public getSrcFromPath(path: string) {
    return Capacitor.convertFileSrc(path)
  }

  async DeviceReady(): Promise<void> {
    while (!this.ready)
      await this.sleep(5)
    return;
  }

  async GetDeviceId(): Promise<string> {
    if (this.UUID == "")
      this.UUID = await Device.getId().then((info: DeviceId) => {
        return info.identifier
      }).catch(e => {
        return "420";
      })
    return this.UUID
  }

  async getDeviceInfo() {
    if (this.cachedDeviceInfo) return this.cachedDeviceInfo
    await this.DeviceReady()
    let data = {
      system: "",
      name: "",
      model: "",
      manufacturer: ""
    }
    let info = await Device.getInfo()
    data.system = info.operatingSystem
    data.name = info.name || ""
    data.model = info.model
    data.manufacturer = info.manufacturer
    this.cachedDeviceInfo = data
    return data
  }

  public getVersion() {
    //return "1.0.5"
    return this.version
  }

  /**
       * parse the value to json or return the default given 
       * @param text text to parse
       * @param default the value to retunr if parse has failed
       * @returns Object
       */
  public jsonParse(toParse: string, defaultValue: any, silentWarn = false): any {
    try {
      return JSON.parse(toParse)
    } catch (error) {
      if (!silentWarn) LoggerService.warn('Erreur lors du parsing JSON', error)
      return defaultValue
    }
  }

  isPlatform(plt: 'web' | 'ios' | 'android'): boolean {
    return this.platform == plt;
  }

  async notify(text: string, duration: 'short' | 'long' = 'short') {
    if (this.isPlatform("web")) {
      let ms = duration == "short" ? 2000 : 3500
      await (await this.toastBrowser.create({ message: text, duration: ms, position: "top" })).present()
      await this.sleep(ms)
      return
    };
    return Toast.show({ text: text, duration: duration, position: "top" })
  }

  async openCameraSettings() {
    // if (this.isPlatform('ios')) {
    //   // iOS: Open Camera settings directly
    return await NativeSettings.open({
      optionAndroid: AndroidSettings.Application,
      optionIOS: IOSSettings.App
    });
    // } else if (this.isPlatform('android')) {
    //   // Android: Open Camera settings
    //   this.diagnostic.switchToSettings();
    // }
  }

  /**
   * Scrolls to an element by its ID.
   *
   * @param {string} Id - The ID of the element to scroll to.
   * @param {number} [sleepTime=0] - The sleep time in milliseconds before scrolling.
   */
  async scrollToElementById(Id: string, sleepTime = 0) {
    await this.sleep(sleepTime)
    if (Id.startsWith("#"))
      Id = Id.slice(1)
    const element = document.getElementById(Id)
    if (element) {
      const rect = element.getBoundingClientRect();
      if (!(rect.top >= 0 && rect.bottom <= window.innerHeight)) {

        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }


  /**
   * Asynchronously delays the execution of the program for the specified number of milliseconds.
   *
   * @param {number} ms - The number of milliseconds to delay the program execution.
   */
  async sleep(ms) {
    await new Promise((cb, fa) => {
      setTimeout(cb, ms)
    })
  }

  extractChar(str: string, quantity: number = 1, beginAt: number = 0, startFromEnd: boolean = false) {
    if (startFromEnd) {
      return beginAt > 0 ? str.slice((-beginAt) + (-quantity), -beginAt) : str.slice((-beginAt) + (-quantity))
    } else
      return str.slice(beginAt, beginAt + quantity)
  }

  capitalizeFirstChar(Str: string): string {
    return Str.charAt(0).toUpperCase() + Str.slice(1);
  }


  minimilizeFirstChar(Str: string): string {
    return Str.charAt(0).toLowerCase() + Str.slice(1);
  }

  parse(Str, Replace: object = {}): string {
    if (Str == null) Str = "";
    for (const [lookFor, replaceBy] of Object.entries(Replace))
      Str = Str.replaceAll(`{${lookFor}}`, replaceBy);
    return Str;
  }

  async keepAwake() {
    if (this.keepAwakeIsSupported())
      return KeepAwake.keepAwake()
  }

  async allowSleep() {
    if (this.keepAwakeIsSupported())
      return KeepAwake.allowSleep()
  }

  async keepAwakeIsSupported() {
    return (await KeepAwake.isSupported()).isSupported
  }

  /**
   * return get arguments as readable object from the given link with given separator
   * @param link link to parse
   * @param separator separtor between link and get arguments
   * @return get arguments as readable object
   */
  linkParser(link: string, separator: string = "/?"): any {
    let parsed: any = {}// définie un object vide
    link.split(separator)// split le lien sur un séparateur donné
      .pop()// sort les arguments aprés le /? 
      .split("&")//separ arg1=1&arg2=2 etc... en chaine de ["arg1=1", "arg2=2", ....]
      .map(splited => splited.split("="))// remap la chaine de ["arg1=1", "arg2=2", ....] en [[arg1,1],[arg2,2], ....]
      .forEach(([properties, value]) => {
        parsed[this.capitalizeFirstChar(properties)] = value// capitalize first char to keep in right format the return object
      });
    return parsed;
  }

  encrypt(toEncrypt) {
    return CryptoJS.HmacSHA512(toEncrypt, this.UUID).toString();
  }

}
